export type VideoGridLayout = {
  cols: number;
  rows: number;
  tileWidthPx: number;
  tileHeightPx: number;
};

export type Args = {
  gridSize: number;
  containerWidth: number;
  containerHeight: number;
  aspectRatio?: number;
  gapPx?: number;
};

const EMPTY_LAYOUT: VideoGridLayout = {
  cols: 1,
  rows: 1,
  tileWidthPx: 0,
  tileHeightPx: 0,
};

function isPositiveFinite(n: number): boolean {
  return Number.isFinite(n) && n > 0;
}

export function computeVideoGridLayout({
  gridSize,
  containerWidth,
  containerHeight,
  aspectRatio = 16 / 9,
  gapPx = 16,
}: Args): VideoGridLayout {
  if (
    !isPositiveFinite(gridSize) ||
    !isPositiveFinite(containerWidth) ||
    !isPositiveFinite(containerHeight) ||
    !isPositiveFinite(aspectRatio)
  ) {
    return EMPTY_LAYOUT;
  }

  let best: VideoGridLayout = EMPTY_LAYOUT;
  let bestArea = -Infinity;

  for (let cols = 1; cols <= gridSize; cols++) {
    const rows = Math.ceil(gridSize / cols);
    const availW = containerWidth - gapPx * (cols - 1);
    const availH = containerHeight - gapPx * (rows - 1);

    if (availW <= 0 || availH <= 0) continue;

    const wFromWidth = availW / cols;
    const wFromHeight = (availH / rows) * aspectRatio;
    const tileWidthPx = Math.max(0, Math.min(wFromWidth, wFromHeight));
    const tileHeightPx = tileWidthPx / aspectRatio;

    const area = tileWidthPx * tileHeightPx;
    if (area > bestArea) {
      bestArea = area;
      best = { cols, rows, tileWidthPx, tileHeightPx };
    }
  }

  return best;
}

export default computeVideoGridLayout;
